// src/services/goalMonthClose.js
//
// Closing a month's goal progress: a signed snapshot of both goals' counts.
// It never resets or changes a count (a new month is empty because counts are
// keyed by month). Used by the Coordinator page's Close button and by the
// "Close last month?" pop-up (MonthClosePrompt), so both write exactly the
// same documents:
//   coordinatorEnrollmentArchives/{month}-{timestamp}   the snapshot (both goals)
//   coordinatorEnrollments/{month}                      closedAt / closedBy / closedTotal
//   coordinatorScreenings/{month}                       the same, separately, so a
//                                                       failure here can't undo the close
// Everything is under the active company + site (firestorePaths helpers).

import { collection, doc, getDoc, getDocs, limit, query, serverTimestamp, setDoc, where, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import {
  getCoordinatorEnrollmentArchivesPath,
  getCoordinatorEnrollmentsPath,
  getCoordinatorScreeningsPath,
  getEnrollmentPeriodPath,
  getScreeningPeriodPath,
  getWeeklyReportsPath,
} from "./firestorePaths";
import { fetchActiveCoordinators } from "./coordinatorService";
import { mergeLeaderboard } from "./goalProgress";
import { periodLabel, PERIOD_TYPES } from "./enrollmentPeriods";

const snapshotOf = (rows = []) => {
  const coordinators = rows.map((c) => ({ name: c.name, count: Math.max(Number(c.count) || 0, 0) }));
  return { coordinators, total: coordinators.reduce((sum, c) => sum + c.count, 0) };
};

/**
 * Write the close for a month from the leaderboard rows already in hand
 * ([{ name, count }] per goal). Returns { total, screeningTotal }.
 */
export const writeMonthClose = async ({ monthId, enrollmentRows, screeningRows, by }) => {
  const who = by || "";
  const now = new Date();
  const [cy, cm] = monthId.split("-").map(Number);
  const archiveId = `${monthId}-${now.toISOString().replace(/[:.]/g, "-")}`;
  const label = periodLabel(PERIOD_TYPES.MONTH, monthId) || monthId;
  const enroll = snapshotOf(enrollmentRows);
  const screen = snapshotOf(screeningRows);

  const batch = writeBatch(db);
  // The audit snapshot, stamped with the month it COVERS.
  batch.set(doc(db, getCoordinatorEnrollmentArchivesPath(), archiveId), {
    archiveId,
    monthId,
    year: cy,
    month: cm,
    label,
    total: enroll.total,
    coordinators: enroll.coordinators,
    screeningTotal: screen.total,
    screeningCoordinators: screen.coordinators,
    archivedAt: serverTimestamp(),
    archivedBy: who,
  });
  // The close stamp lives on the PERIOD document (where the leaderboard looks).
  batch.set(
    doc(db, getCoordinatorEnrollmentsPath(), monthId),
    { periodId: monthId, closedAt: serverTimestamp(), closedBy: who, closedTotal: enroll.total, updatedAt: serverTimestamp() },
    { merge: true }
  );
  await batch.commit();

  // The screening period separately, so the enrollment close can't fail because of it.
  try {
    await setDoc(
      doc(db, getCoordinatorScreeningsPath(), monthId),
      { periodId: monthId, closedAt: serverTimestamp(), closedBy: who, closedTotal: screen.total, updatedAt: serverTimestamp() },
      { merge: true }
    );
  } catch (err) {
    console.error("❌ Error stamping the screening month as closed:", err);
  }
  return { total: enroll.total, screeningTotal: screen.total };
};

/** The leaderboard rows for a month (same roster rule as the Coordinator page). */
export const loadMonthCloseRows = async (monthId) => {
  const [coordinators, enrollSnap, screenSnap] = await Promise.all([
    fetchActiveCoordinators(),
    getDocs(collection(db, getEnrollmentPeriodPath(monthId))),
    getDocs(collection(db, getScreeningPeriodPath(monthId))).catch(() => ({ docs: [] })),
  ]);
  const roster = coordinators.filter((c) => c.includeInLeaderboard !== false).map((c) => c.name);
  const rows = (snap) => mergeLeaderboard(snap.docs.map((d) => ({ name: d.id, ...d.data() })), roster).rows;
  return { enrollmentRows: rows(enrollSnap), screeningRows: rows(screenSnap) };
};

/**
 * Whether a month is closed, and whether it had anything to close: any
 * goal count, or any weekly report filed for it.
 */
export const loadMonthCloseStatus = async (monthId) => {
  const [parentSnap, enrollSnap, screenSnap, reportSnap] = await Promise.all([
    getDoc(doc(db, getCoordinatorEnrollmentsPath(), monthId)),
    getDocs(collection(db, getEnrollmentPeriodPath(monthId))),
    getDocs(collection(db, getScreeningPeriodPath(monthId))).catch(() => ({ docs: [] })),
    getDocs(query(collection(db, getWeeklyReportsPath()), where("monthId", "==", monthId), limit(1))).catch(() => ({ docs: [] })),
  ]);
  const anyCount = (snap) => snap.docs.some((d) => (Number(d.data()?.count) || 0) > 0);
  return {
    closed: !!(parentSnap.exists() && parentSnap.data()?.closedAt),
    hasData: anyCount(enrollSnap) || anyCount(screenSnap) || reportSnap.docs.length > 0,
  };
};
